import { LicenseType } from '../../api/gqty/schema.generated';
import { UpsellFeature } from '../../ui/upsell/upsell.component';

/** Static configuration for a gated (not-in-plan) module's upsell page. */
export interface GatedModuleConfig {
  /** Material Symbols icon, matches the sidebar nav icon. */
  icon: string;
  /** Translation key for the module name (reused as the sidebar label). */
  titleKey: string;
  /** Translation key for the supporting paragraph. */
  leadKey: string;
  /** Value points describing what the module does. */
  features: readonly UpsellFeature[];
  /**
   * License the "contact sales" CTA asks to activate. Set for modules backed by
   * a real license type; roadmap-only modules fall back to {@link LicenseType.CUSTOM}
   * so the request still identifies the section it came from.
   */
  module: LicenseType;
}

const f = (icon: string, base: string): UpsellFeature => ({
  icon,
  titleKey: `${base}.title`,
  descKey: `${base}.desc`,
});

/**
 * Single source of truth for every gated module. The route uses `key` to look
 * up the config; the sidebar uses `titleKey`/`icon` for its entry. Adding a
 * module is: one entry here + one nav item + the i18n strings.
 */
export const GATED_MODULES: Record<string, GatedModuleConfig> = {
  financial: {
    icon: 'payments',
    titleKey: 'shell.nav.financial',
    leadKey: 'upsell.financial.lead',
    features: [
      f('request_quote', 'upsell.financial.f1'),
      f('monitoring', 'upsell.financial.f2'),
      f('account_balance_wallet', 'upsell.financial.f3'),
    ],
    module: LicenseType.CUSTOM,
  },
  customers: {
    icon: 'contacts',
    titleKey: 'shell.nav.customers',
    leadKey: 'upsell.customers.lead',
    features: [
      f('badge', 'upsell.customers.f1'),
      f('history', 'upsell.customers.f2'),
      f('hub', 'upsell.customers.f3'),
    ],
    module: LicenseType.CUSTOM,
  },
  live: {
    icon: 'my_location',
    titleKey: 'shell.nav.live',
    leadKey: 'upsell.live.lead',
    features: [
      f('near_me', 'upsell.live.f1'),
      f('smartphone', 'upsell.live.f2'),
      f('photo_camera', 'upsell.live.f3'),
      f('share_location', 'upsell.live.f4'),
    ],
    module: LicenseType.EVERPLAN_LIVE,
  },
  analytics: {
    icon: 'query_stats',
    titleKey: 'shell.nav.analytics',
    leadKey: 'upsell.analytics.lead',
    features: [
      f('speed', 'upsell.analytics.f1'),
      f('groups', 'upsell.analytics.f2'),
      f('dashboard_customize', 'upsell.analytics.f3'),
      f('download', 'upsell.analytics.f4'),
    ],
    module: LicenseType.CUSTOM,
  },
  ai: {
    icon: 'auto_awesome',
    titleKey: 'shell.nav.ai',
    leadKey: 'upsell.ai.lead',
    features: [
      f('forum', 'upsell.ai.f1'),
      f('smart_toy', 'upsell.ai.f2'),
      f('bolt', 'upsell.ai.f3'),
    ],
    module: LicenseType.CUSTOM,
  },
  inventory: {
    icon: 'inventory_2',
    titleKey: 'shell.nav.inventory',
    leadKey: 'upsell.inventory.lead',
    features: [
      f('category', 'upsell.inventory.f1'),
      f('inventory', 'upsell.inventory.f2'),
      f('build', 'upsell.inventory.f3'),
      f('notifications_active', 'upsell.inventory.f4'),
    ],
    module: LicenseType.CUSTOM,
  },
  documents: {
    icon: 'folder_open',
    titleKey: 'upsell.documents.name',
    leadKey: 'upsell.documents.lead',
    features: [
      f('description', 'upsell.documents.f1'),
      f('verified', 'upsell.documents.f2'),
      f('event_available', 'upsell.documents.f3'),
    ],
    module: LicenseType.CUSTOM,
  },
  connect: {
    icon: 'hub',
    titleKey: 'upsell.connect.name',
    leadKey: 'upsell.connect.lead',
    features: [
      f('vpn_key', 'upsell.connect.f1'),
      f('webhook', 'upsell.connect.f2'),
      f('sync_alt', 'upsell.connect.f3'),
    ],
    module: LicenseType.EVERPLAN_CONNECT,
  },
  transportation: {
    icon: 'local_shipping',
    titleKey: 'shell.nav.transportation',
    leadKey: 'upsell.transportation.lead',
    features: [
      f('merge', 'upsell.transportation.f1'),
      f('warehouse', 'upsell.transportation.f2'),
      f('local_shipping', 'upsell.transportation.f3'),
      f('scale', 'upsell.transportation.f4'),
    ],
    module: LicenseType.EVERPLAN_TRANSPORTATION,
  },
  planning: {
    icon: 'calendar_month',
    titleKey: 'upsell.planning.name',
    leadKey: 'upsell.planning.lead',
    features: [
      f('auto_fix_high', 'upsell.planning.f1'),
      f('groups', 'upsell.planning.f2'),
      f('tune', 'upsell.planning.f3'),
    ],
    module: LicenseType.EVERPLAN_PLANNING,
  },
};

/**
 * Maps gated tab keys (reused across the resource upsert dialogs) to the gated
 * module whose upsell panel they show. Keeps the gating story consistent
 * between the sidebar, work-package tabs and resource tabs.
 */
export const GATED_RESOURCE_TABS = {
  performance: 'analytics',
  assignments: 'live',
  documents: 'documents',
} as const satisfies Record<string, keyof typeof GATED_MODULES>;

export type GatedResourceTab = keyof typeof GATED_RESOURCE_TABS;

/** Returns the upsell config for a gated resource tab, if it is one. */
export function gatedResourceConfig(tab: string): GatedModuleConfig | undefined {
  const key = (GATED_RESOURCE_TABS as Record<string, keyof typeof GATED_MODULES>)[tab];
  return key ? GATED_MODULES[key] : undefined;
}
